import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import updateLocale from 'dayjs/plugin/updateLocale';
import weekday from 'dayjs/plugin/weekday';

import { FieldType } from '~components/form-fields/props';

dayjs.extend(customParseFormat);
dayjs.extend(isBetweenPlugin);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(updateLocale);
dayjs.extend(weekday);
dayjs.extend(quarterOfYear);

// static format variations

// preferred API formats
export const dateInputFieldFormat = 'YYYY-MM-DD';
export const timeValueFormat = 'HH:mm';
export const defaultMinuteInterval = 5;

// preferred FE display formats
export const dateDisplayFormat = 'DD/MMM/YYYY';
export const datePickerDisplayFormat = 'DD/MM/YYYY';
export const monthDisplayFormat = 'MMMM YYYY';
export const timeDisplayFormat = 'hh:mm A';
export const weeklyPlannerDayDisplayFormat = 'ddd (DD/MMM)';
export const dateTimeDisplayFormat = 'DD/MMM/YYYY hh:mm A';

// legacy garbage (╯°□°）╯︵ ┻━┻
const weekdayFormat = 'dddd';
export const dayFormatShort = 'ddd';
export const timeFormatWithSeconds = 'HH:mm:ss';
export const catchAllStartDate = '1990-01-01';
export const catchAllEndDate = '2050-12-31';

export { Dayjs };

export const formatDateTimeValue = (type: FieldType | undefined, value: any) => {
  switch (type) {
    case 'date': {
      if (value?.length == 10) return dayjs(value).format(dateInputFieldFormat);
      return value;
    }
    case 'time': {
      if (value?.length == 5) return dayjs(value, timeValueFormat).format(timeFormatWithSeconds);
      return value;
    }
    default: {
      return value;
    }
  }
};

export const formatWeekday = (dayIndex: number, useShortFormat = false): string => {
  const formatType = useShortFormat ? dayFormatShort : weekdayFormat;
  return dayjs().day(dayIndex).format(formatType);
};

export const getDayString = (dateValue: any): string =>
  dateValue ? dayjs(dateValue.toString()).format(dayFormatShort) : '';

export const getFullDayString = (dateValue: any): string =>
  dateValue ? dayjs(dateValue.toString()).format(weekdayFormat) : '';

export function getNextMonday() {
  const today = dayjs();
  const monday = today.day() === 1 ? today.add(1, 'week') : today.day(8);
  return monday.format(dateInputFieldFormat);
}

export const getQuarterEndDate = (dateValue: any): string =>
  dateValue ? dayjs(dateValue.toString()).endOf('quarter').format(dateInputFieldFormat) : '';

interface DayOption {
  value: string | number;
  label: string;
}

export function getDaysMultiSelectOptions(includePublicHoliday = false): DayOption[] {
  const weekStart = dayjs().weekday(0);
  const days = Array.from({ length: 7 }, (_, i) => ({
    value: i,
    label: weekStart.add(i, 'day').format('ddd'),
  }));

  // Add Public Holiday option
  if (includePublicHoliday) {
    days.push({ value: 7, label: 'Public Holiday' });
  }

  return days;
}

export const formatDayString = (dateValue: any): string =>
  dateValue ? dayjs(`${dateValue.toString()}`, 'YYYY-MM-DD HH:mm').format(dateDisplayFormat) : '';
export const formatDayTimeString = (dateValue: any): string =>
  dateValue ? dayjs(`${dateValue.toString()}`, 'YYYY-MM-DD HH:mm').format(dateTimeDisplayFormat) : '';
export const formatTimeStringAMPM = (dateValue: any): string =>
  dateValue ? dayjs(`${dateValue.toString()}`, 'YYYY-MM-DD HH:mm').format(timeDisplayFormat) : '';
export const formatDateString = (dateValue: any): string =>
  dateValue ? dayjs(dateValue.toString()).format(dateDisplayFormat) : '';
export const dateAsIso = (dateValue: any) => dayjs(dateValue).toISOString();
export const formatTimeString = (timeValue: any): string => {
  if (!timeValue) return '';
  let parsedTime;

  if (dayjs.isDayjs(timeValue)) {
    parsedTime = timeValue;
  } else {
    const formattedValue = formatDateTimeValue('time', timeValue.toString());
    parsedTime = dayjs(formattedValue, timeValueFormat);
  }

  return parsedTime.isValid() ? parsedTime.format(timeDisplayFormat) : '';
};

export const getTime = (timeValue: any, offsetMinutes?: number, date?: string): Dayjs => {
  let timeVal = dayjs(formatDateTimeValue('time', timeValue), timeValueFormat);
  if (date) timeVal = dayjs(`${date} ${timeValue}`, 'YYYY-MM-DD HH:mm');
  if (!offsetMinutes) return timeVal;
  return timeVal.add(offsetMinutes, 'minutes');
};

export const getDate = (value: any) => dayjs(value).format(dateInputFieldFormat);

// Check if the first day is in the same week of the second day
export const isInSameWeek = (a: Dayjs, b: Dayjs | string) => (b === null ? false : a.isSame(b, 'week'));
export const isBeforeDay = (a: Dayjs | string, b: Dayjs | string) => (b === null ? false : dayjs(a).isBefore(b, 'day'));
export const isInSameOrAfterWeek = (a: Dayjs, b: Dayjs | string) => (b === null ? false : a.isSameOrAfter(b, 'week'));

export const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const units = [];
  if (hours > 0) units.push(`${hours}h`);
  const displayMinutes = Math.floor(minutes - hours * 60);
  units.push(`${displayMinutes}m`);
  return units.join(' ');
};

// Format time to HH:mm string
export const formatHourMinutes = (total: number) => {
  const hours = Math.floor(total / 60);
  const minutes = total % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const formatDefaultSearchDate = (date: Dayjs) => {
  const start_date = date.subtract(30, 'days').format(dateInputFieldFormat);

  return { gte: start_date };
};

export const getRepeatEndDate = (repeat_end: string) => {
  if (repeat_end && repeat_end !== '') {
    if (dayjs(catchAllEndDate) < dayjs(repeat_end)) {
      repeat_end = '';
    }
  }
  return formatDateString(repeat_end);
};

export const validateDate = (date: string) => {
  const parsedDate = dayjs(date, dateInputFieldFormat, true);
  return (
    parsedDate.isValid() && parsedDate.isSameOrAfter(catchAllStartDate) && parsedDate.isSameOrBefore(catchAllEndDate)
  );
};

type Derive = (values: any) => any;
export const deriveDuration: Derive = ({ start_time, end_time, ends_next_day = false }) => {
  const start = getTime(start_time);
  const end = ends_next_day ? getTime(end_time).add(1, 'day') : getTime(end_time);
  const diff = end.diff(start, 'minutes');
  return Number.isFinite(diff) ? diff : 0;
};

export const deriveDateRange = (date: string, rangeType: 'Daily' | 'Weekly') => {
  if (rangeType === 'Daily') {
    const start = dayjs(date).format(dateInputFieldFormat);
    const end = dayjs(date).add(1, 'day').format(dateInputFieldFormat);
    return { week_start: start, week_end: end };
  }
  const ws = dayjs(date).weekday(0);
  return {
    week_start: ws.format(dateInputFieldFormat),
    week_end: ws.add(6, 'day').format(dateInputFieldFormat),
  };
};

export const getMonthDateRange = (month: string) => ({
  startDate: dayjs(month).startOf('month').format(dateInputFieldFormat),
  endDate: dayjs(month).endOf('month').format(dateInputFieldFormat),
});

const extendedDayjs = dayjs;
export default extendedDayjs;
